import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export interface AWSAccount {
  id: string;
  name: string;
}

export interface CostDetail {
  accountId: string;
  accountName: string;
  currentMonthCost: number;
  lastMonthCost: number;
  trend: number;
  dailyCosts: Array<{ date: string; cost: number }>;
  serviceBreakdown: Array<{
    service: string;
    cost: number;
    percentage: number;
  }>;
  resourceCosts: Array<{
    resource: string;
    service: string;
    cost: number;
    tags: Record<string, string>;
  }>;
}

export interface FinOpsApi {
  getAccounts(): Promise<AWSAccount[]>;
  getCostDetail(accountId: string): Promise<CostDetail>;
}

export const finopsApiRef = createApiRef<FinOpsApi>({
  id: 'plugin.finops.service',
});

export class FinOpsClient implements FinOpsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getAccounts(): Promise<AWSAccount[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('finops');
    const response = await this.fetchApi.fetch(`${baseUrl}/accounts`);

    if (!response.ok) {
      throw new Error(`Failed to fetch accounts: ${response.statusText}`);
    }

    const data = await response.json();
    return data.accounts || [];
  }

  async getCostDetail(accountId: string): Promise<CostDetail> {
    const baseUrl = await this.discoveryApi.getBaseUrl('finops');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/accounts/${accountId}/costs`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch cost details: ${response.statusText}`);
    }

    return await response.json();
  }
}
